<template>
  <div class="workflow-list">
    <div class="header">
      <div class="title-wrap">
        <h3>Workflows</h3>
        <span class="meta-count">{{ (workflows || []).length }} item(s)</span>
      </div>
      <button @click="navigateToCreateWorkflow" class="new-workflow-button hide-on-collapse">
        New Workflow
      </button>
    </div>

    <div class="search-filter-row">
      <input type="text" v-model="searchQuery" placeholder="Search by Name..." class="search-input">
      <button @click="showFilters = !showFilters" class="toggle-filter-button">
        {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
      </button>
    </div>

    <!-- Collapsible Filter Menu -->
    <div v-if="showFilters" class="filters">
      <input type="date" v-model="startDate" class="date-input" placeholder="Start Date">
      <input type="date" v-model="endDate" class="date-input" placeholder="End Date">
    </div>

    <table class="workflow-table">
      <thead>
        <tr>
          <th @click="sortWorkflows('name')">Name<i v-if="currentSortColumn === 'name'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
          <th @click="sortWorkflows('status')">Status<i v-if="currentSortColumn === 'status'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
          <th @click="sortWorkflows('start_date')">Start Date<i v-if="currentSortColumn === 'start_date'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
          <th @click="sortWorkflows('end_date')">End Date<i v-if="currentSortColumn === 'end_date'" class="filter-arrow" :class="setArrowDirection(currentSortDirection)"></i></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="workflow in paginatedWorkflows" :key="workflow.id" @click="navigateToWorkflowDetail(workflow.id)" class="workflow-row">
          <td>{{ workflow.name }}</td>
          <td>{{ workflow.status }}</td>
          <td>{{ formatDate(workflow.start_date) }}</td>
          <td>{{ formatDate(workflow.end_date) }}</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination">
      <button v-for="page in visiblePageNumbers" :key="page" @click="setPage(page)" :class="{ active: page === currentPage }">{{ page }}</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_ENDPOINTS } from '@/services/config.js';

export default {
  props: {
    workflows: {
      type: Array,
      required: true,
    },
    projectId: Number,
    
  },
  data() {
    return {
      searchQuery: '',
      currentPage: 1,
      workflowsPerPage: 5,
      currentSortColumn: 'name',
      currentSortDirection: 'asc',
      showFilters: false,
      startDate: '',
      endDate: '',
      sortedWorkflows: [],
      workflowProjects: {}, 
    };
  },
  created() {
    this.sortedWorkflows = [...this.workflows];
    console.log("THis is the project id: ", this.projectId)
  },
  watch: {
    workflows(newVal) {
      this.sortedWorkflows = [...newVal];
    }
  },
  methods: {
    navigateToCreateWorkflow() {
      this.$router.push({ path: '/create-workflow',
      query: { 
            projectId: this.projectId, 
          } 
       });
    },
    async fetchProjectNames() {
      try {
        const projectIds = [...new Set(this.workflows.map(w => w.project_id))]; 

        for (const projectId of projectIds) {
          if (!projectId) continue;

          const response = await axios.get(API_ENDPOINTS.project(projectId));
      
          this.workflowProjects = { ...this.workflowProjects, [projectId]: response.data.name };
        }
      } catch (error) {
        console.error("Error fetching project names:", error);
      }
    },
    sortWorkflows(column) {
      if (this.currentSortColumn === column) {
        this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.currentSortDirection = 'asc';
        this.currentSortColumn = column;
      }
      this.sortedWorkflows.sort((a, b) => {
        if (a[column] < b[column]) return this.currentSortDirection === 'asc' ? -1 : 1;
        if (a[column] > b[column]) return this.currentSortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    },
    setPage(pageNumber) {
      this.currentPage = pageNumber;
    },
    setArrowDirection(sortDirection) {
      return sortDirection === 'desc' ? "bi bi-arrow-down" : "bi bi-arrow-up";
    },
    navigateToWorkflowDetail(workflowId) {
      this.$router.push({ name: 'WorkflowDetailPage', params: { id: workflowId } });
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
  },
  computed: {
    filteredWorkflows() {
      return this.sortedWorkflows.filter(workflow => {
        const name = workflow.name?.toLowerCase() || '';
        const matchesSearchQuery = name.includes(this.searchQuery.toLowerCase());
        const matchesStartDate = this.startDate === '' || new Date(workflow.start_date) >= new Date(this.startDate);
        const matchesEndDate = this.endDate === '' || new Date(workflow.end_date) <= new Date(this.endDate);
        return matchesSearchQuery && matchesStartDate && matchesEndDate;
      });
    },
    paginatedWorkflows() {
      const start = (this.currentPage - 1) * this.workflowsPerPage;
      const end = start + this.workflowsPerPage;
      return this.filteredWorkflows.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredWorkflows.length / this.workflowsPerPage);
    },
    visiblePageNumbers() {
      const range = 10;
      let startPage = Math.max(1, this.currentPage - Math.floor(range / 2));
      let endPage = startPage + range - 1;
      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - range + 1);
      }
      return Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.workflow-list {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 26px;
    padding-right: 58px;

    h3 {
      margin: 0;
      color: $primary-color;
    }

    .title-wrap {
      display: flex;
      align-items: baseline;
      gap: 14px;
    }

    .meta-count {
      font-size: 0.92rem;
      font-weight: 500;
      color: rgba($primary-color, 0.8);
    }

    .new-workflow-button {
      padding: 8px 16px;
      background-color: $primary-color;
      color: #fff;
      border: none;
      border-radius: 5px;
      cursor: pointer;
    }

    .new-workflow-button:hover {
      background-color: darken($primary-color, 10%);
    }
  }

  .search-filter-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
  }

  .search-input {
    flex: 1;
    margin: 0;
  }

  .toggle-filter-button {
    width: auto;
    margin: 0;
    padding: 6px 10px;
    white-space: nowrap;
  }

  .workflow-table {
      width: 100%;
  }

  .workflow-table th {
    background-color: $form-color;
    border: 1px solid $primary-color;
    cursor: pointer;
  }

  .filters {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: none;
    margin: 6px 0;
    gap: 8px;
  }

  .workflow-row {
    background-color: $secondary-color;
    cursor: pointer;
  }

  .workflow-row:hover {
    background-color: $form-color;
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }

  .pagination button {
    margin: 0 5px;
    padding: 5px 10px;
    background-color: $primary-color;
    border: 1px solid $secondary-color;
    border-radius: 5px;
    cursor: pointer;
  }

  .pagination button:hover,
  .pagination button.active {
    background-color: darken($primary-color, 10%);
  }
}

</style>
